import React, { useRef, useState, useEffect } from 'react';
import { useMemo } from 'react';

import addSvg from '@assets/img/icons/add.svg';
import deleteSvg from '@assets/img/icons/delete.svg';
import horizontalSwitchSvg from '@assets/img/icons/horizontal-switch.svg';
import infoSvg from '@assets/img/icons/info.svg';
import { ErrorMessages } from '@components/NextForms/ErrorMessages';
import { TextInput } from '@components/NextForms/TextInput';
import { bookingInquiryStrings } from '@data/bookingInquiry';
import { getReceivingCityOptionInlineDisplay, receivingCityOptions } from '@data/cities';
import { useIsAdmin } from '@hooks/useIsAdmin';
import IconButton from '@material-ui/core/IconButton';
import Popper from '@material-ui/core/Popper';
import { BookingInquiryCargoForm } from '@models/next/BookingInquiryForms';
import { BookingInquiryHelper } from '@models/next/BookingInquiryHelper';
import {
  MobxZodArrayField,
  MobxZodDiscriminatedUnionField,
  MobxZodField,
  empty,
  getDecodeResultOr,
  mapDecodeResult,
} from '@monoid-dev/mobx-zod-form';
import { makeMuiStyles, useCommonStyles } from '@styles/index';
import { createStrings } from '@utils/local';
import { iota } from '@utils/types';
import clsx from 'clsx';
import { observer } from 'mobx-react';
import useHoverDirty from 'react-use/lib/useHoverDirty';
import * as R from 'remeda';
import { z } from 'zod';

const strings = createStrings({
  ja: {
    selectPortPlaceholder: '倉庫を選んでください',
    english: '英語',
    cargoNumHintString:
      '梱包後の貨物個数をご記入ください。複数の貨物をパレットなどににまとめている場合は荷姿で数えてください。',
    computeByLWH: 'LWH',
    computeByWV: '総重',
    addCargo: '出荷物を追加',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  cargosListTable: {
    ...common.middleText,
    display: 'grid',
    overflowX: 'scroll',
    overflowY: 'visible',
    gridTemplateColumns:
      'auto 2fr minmax(min-content, 1fr) minmax(min-content, 1fr) minmax(min-content, 1fr) max-content',
    '& > *[role="head"]': {
      ...common.textGray500,
      ...common.fontWeight600,
      ...common.backgroundPrimaryAlpha5,
    },
    '& > div[role="data"]': {
      paddingTop: 16,
      paddingLeft: 9,
      paddingRight: 9,
    },
    '& > div': {
      paddingTop: 10,
      paddingBottom: 10,
      paddingLeft: 9,
      paddingRight: 9,
    },
  },
  cargosSummaryContainer: {
    ...common.flexInline,
    ...common.itemsCenter,
    ...common.middleText,
    ...common.backgroundPrimaryAlpha5,
    padding: '1rem',
    [theme.breakpoints.down('xs')]: {
      ...common.flexColumn,
      alignItems: 'flex-start',
      rowGap: '0.3rem',
    },
  },
  actionButton: {
    ...common.baseButton,
    ...common.flexInline,
    ...common.itemsCenter,
    ...common.textPrimary,
  },
  lwhSplit: {
    ...common.textGray500,
    paddingTop: 4,
    paddingLeft: 4,
    paddingRight: 4,
  },
  label: {
    width: 150,
  },
  receivingCityGroupInput: {},
  cargoNumHintContainer: {
    ...common.baseButton,
    display: 'inline',
    position: 'relative',
    marginLeft: '0.5rem',
    top: 1,
  },
  hintContainer: {
    ...common.exSmallerText,
    ...common.textGray700,
    marginLeft: '0.5rem',
    marginRight: '0.5rem',
    padding: 9,
    lineHeight: 1.75,
    backgroundColor: '#F2F7F8',
    width: 300,
    border: `1px solid ${theme.palette.primary.main}`,
  },
}));

export const CargosPortNameInput: React.FC<{ field: MobxZodField<z.ZodString> }> = ({ field }) => {
  const options = useMemo(
    () =>
      receivingCityOptions.map((g) => ({
        value: g.name,
        label: getReceivingCityOptionInlineDisplay(g),
      })),
    [],
  );

  return (
    <TextInput
      field={field}
      inputOptions={{
        type: 'choose',
        options,
      }}
      inputProps={{
        placeholder: strings.selectPortPlaceholder,
      }}
    />
  );
};

const CargoRow: React.FC<{
  index: number;
  field: MobxZodDiscriminatedUnionField<typeof BookingInquiryCargoForm>;
  onDelete: () => void;
  hasPickups: boolean;
  pickupAddressCount: number;
}> = observer(function CargoRow({ index, field, onDelete, hasPickups, pickupAddressCount }) {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { fieldsResult } = field;

  if (!fieldsResult.success) {
    return null;
  }

  return (
    <>
      {!hasPickups && (
        <div style={{ paddingLeft: 20 }} role="data">
          {index + 1}{' '}
        </div>
      )}

      {hasPickups && (
        <div style={{ paddingLeft: 14 }}>
          <TextInput
            field={fieldsResult.fields.pickup_address_index}
            inputOptions={{
              type: 'choose',
              options: [
                { value: 0, label: '-' },
                ...iota(pickupAddressCount, 1).map((index) => ({
                  value: index,
                  label: `${bookingInquiryStrings.pickupAddress} ${index}`,
                })),
              ],
            }}
          />
        </div>
      )}

      <div>
        <TextInput field={fieldsResult.fields.name} />
      </div>

      <div>
        <TextInput field={fieldsResult.fields.num} withErrorMessages={false} />
      </div>

      {fieldsResult.fields.discriminator === 'WV' && (
        <>
          <div>
            <TextInput field={fieldsResult.fields.total_weight} withErrorMessages={false} />
          </div>
          <div>
            <TextInput field={fieldsResult.fields.total_volume} withErrorMessages={false} />
          </div>
        </>
      )}

      {fieldsResult.fields.discriminator === 'LWH' && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr auto 1fr' }}>
            <div>
              <TextInput field={fieldsResult.fields.length} withErrorMessages={false} />
            </div>
            <div className={classes.lwhSplit}>{':'}</div>
            <div>
              <TextInput field={fieldsResult.fields.width} withErrorMessages={false} />
            </div>
            <div className={classes.lwhSplit}>{':'}</div>
            <div>
              <TextInput field={fieldsResult.fields.height} withErrorMessages={false} />
            </div>
          </div>
          <div>
            <TextInput field={fieldsResult.fields.weight} withErrorMessages={false} />
          </div>
        </>
      )}

      <div
        className={clsx(classes.flexInline, classes.justifyEnd)}
        style={{ columnGap: '1rem', paddingRight: 20 }}
      >
        <button
          type="button"
          onClick={onDelete}
          className={clsx(classes.flexColumn, classes.textPrimary)}
          style={{ paddingTop: 5 }}
        >
          <img src={deleteSvg} alt="Delete" />
        </button>
      </div>
    </>
  );
});

const CargoNumHint: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const infoRef = useRef<HTMLImageElement>(null);

  const isHoveringInfo = useHoverDirty(infoRef);

  const [anchorEl, setAnchorEl] = useState<HTMLImageElement | null>(null);

  useEffect(() => {
    setAnchorEl(infoRef.current);
  }, [isHoveringInfo]);

  return (
    <div className={classes.cargoNumHintContainer}>
      <img alt="Info" src={infoSvg} ref={infoRef} />

      <Popper open={isHoveringInfo} anchorEl={anchorEl} placement="right-start">
        <div className={classes.hintContainer}>{strings.cargoNumHintString}</div>
      </Popper>
    </div>
  );
};

export const CargosListInput: React.FC<{
  /**
   * The input for pickup address might be generic,
   * but only the count is needed.
   */
  pickupAddressCount: number;
  hasPickupsField: MobxZodField<z.ZodBoolean>;
  cargosField: MobxZodArrayField<z.ZodArray<typeof BookingInquiryCargoForm>>;
}> = observer(function CargosListInput({ pickupAddressCount, hasPickupsField, cargosField }) {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const isAdmin = useIsAdmin();

  const hasPickups = getDecodeResultOr(hasPickupsField.decodeResult, false);

  const tag =
    cargosField.elements.length > 0
      ? getDecodeResultOr(cargosField.elements[0].discriminatorField.decodeResult, 'WV')
      : 'WV';

  const onAddCargo = () => {
    cargosField.push(BookingInquiryHelper.getCargoFormDefaultValue(tag));
  };

  const onChangeMeasure = () => {
    const nextTag = tag === 'WV' ? 'LWH' : 'WV';

    if (cargosField.length === 0) {
      cargosField.setOutput([BookingInquiryHelper.getCargoFormDefaultValue(nextTag)]);
    } else {
      cargosField.elements.forEach((elementField) => {
        if (elementField.fieldsResult.success) {
          const { fields } = elementField.fieldsResult;
          const [name, num, pickup_address_index] = [
            getDecodeResultOr(fields.name.decodeResult, ''),
            getDecodeResultOr(fields.num.decodeResult, empty),
            getDecodeResultOr(fields.pickup_address_index.decodeResult, empty),
          ];

          elementField.discriminatorField.setOutput(nextTag);

          elementField.setOutput(
            BookingInquiryHelper.mapCargoFormToTag(nextTag, {
              name,
              num,
              pickup_address_index,
            }),
          );
        }
      });
    }
  };

  const renderHead = () => (
    <>
      <div role="head" style={{ paddingLeft: 20 }}>
        {hasPickups ? bookingInquiryStrings.pickupAddress : 'NO'}
      </div>
      <div role="head">
        {bookingInquiryStrings.cargoName} ({strings.english})
      </div>
      <div role="head">
        {bookingInquiryStrings.cargoNum}
        <CargoNumHint />
      </div>

      {tag === 'WV' && (
        <>
          <div role="head">{bookingInquiryStrings.totalWeight}</div>
          <div role="head">{bookingInquiryStrings.totalVolume}</div>
        </>
      )}

      {tag === 'LWH' && (
        <>
          <div role="head">{bookingInquiryStrings.cargoSize}</div>
          <div role="head">{bookingInquiryStrings.weight}</div>
        </>
      )}

      {!isAdmin && (
        <div
          role="head"
          style={{ paddingRight: 20, paddingLeft: 0 }}
          className={clsx(classes.baseButton, classes.textPrimaryImportant)}
          onClick={onChangeMeasure}
        >
          <span style={{ position: 'relative', top: -1 }}>
            <img
              src={horizontalSwitchSvg}
              alt="Switch Icon"
              style={{ marginRight: 8, position: 'relative', top: 3 }}
            />
            {tag === 'WV' ? bookingInquiryStrings.computeByLWH : bookingInquiryStrings.computeByWV}
          </span>
        </div>
      )}

      {isAdmin && (
        <div
          role="head"
          style={{ paddingRight: 20, paddingLeft: 16 }}
          className={clsx(classes.baseButton, classes.textPrimaryImportant)}
          onClick={onChangeMeasure}
        >
          <button
            className={clsx(classes.flexColumn, classes.itemsCenter)}
            style={{ position: 'relative', left: -2, top: -2 }}
            type="button"
          >
            <img
              src={horizontalSwitchSvg}
              alt="Switch Icon"
              style={{ position: 'relative', marginBottom: 6 }}
            />
            <span style={{ fontSize: 8, lineHeight: 0 }}>
              {tag === 'WV' ? strings.computeByLWH : strings.computeByWV}
            </span>
          </button>
        </div>
      )}
    </>
  );

  const renderInputs = () => {
    return cargosField.elements.map((cargoField, index) => (
      <CargoRow
        key={cargoField.uniqueId}
        index={index}
        field={cargoField}
        onDelete={() => cargosField.splice(index, 1, [])}
        hasPickups={hasPickups}
        pickupAddressCount={pickupAddressCount}
      />
    ));
  };

  const renderSummary = () => {
    const summaries = cargosField.elements.flatMap((element) =>
      mapDecodeResult(element.decodeResult, (d) => [BookingInquiryHelper.getCargoSummary(d)], []),
    );

    const unit = R.sumBy(summaries, (s) => s.unit);
    const cbm = R.sumBy(summaries, (s) => s.cbm);
    const kg = R.sumBy(summaries, (s) => s.kg);

    return (
      <div className={clsx(classes.cargosSummaryContainer)}>
        <div className={classes.actionButton} onClick={onAddCargo}>
          <IconButton>
            <img src={addSvg} alt="Add" />
          </IconButton>
          {strings.addCargo}
        </div>

        <div style={{ flex: 1 }} />
        <div className={classes.textGray700}>
          Shipment Total: {unit ?? '-'} UNITS, {cbm?.toFixed(3) ?? '-'} CBM, {kg?.toFixed(3) ?? '-'}{' '}
          KGS
        </div>
      </div>
    );
  };

  return (
    <div tabIndex={0} ref={(e) => (cargosField.element = e)}>
      <div className={classes.cargosListTable}>
        {renderHead()}
        {renderInputs()}
      </div>
      {cargosField.elements.length === 0 && <div style={{ height: 10 }} />}
      {renderSummary()}

      <div style={{ height: '0.5rem' }} />

      <ErrorMessages field={cargosField} />
    </div>
  );
});
